<script lang="ts" setup>
import { computed, onMounted, Ref, ref } from 'vue';
import { useTranslations } from '~/composables/translations';
import { useReferentialStore } from '~/stores/referential';
import { currencySymbol } from '~/modules/currency';

import vuBtn from '@ds/vuekit/src/components/vu-btn.vue';
import { useTracker } from '~/composables/tracker';
import { useApplicationStore } from '~/application';

// I18N
const { $i18n } = useTranslations('ordering');

// STORES
const referential = useReferentialStore();
const application = useApplicationStore();

// VARIABLES
const primeLabs: Ref<any[]> = ref([]);

// Props definition using defineProps
const props = defineProps({
  lab: {
    type: Object,
    required: true,
  },
  country: {
    type: Object,
    required: true,
  },
  directPurchase: {
    type: Boolean,
    default: false,
  },
  exploreMode: {
    type: Boolean,
    default: false,
  },
  selected: {
    type: Boolean,
    default: false,
  },
  loadingPrice: {
    type: Boolean,
    default: false,
  },
  offerInfo: {
    type: Boolean,
    default: false,
  },
});

// EMITS
const emit = defineEmits(['logo-error', 'toggleSelected', 'more-info', 'buy']);

// Static colors array
const colors = [
  '#078BFA',
  '#F95753',
  '#6FBC4B',
  '#9E846A',
  '#AA4BB2',
  '#1A997B',
  '#F564A3',
  '#FF8A2E',
];

const tracker = useTracker();

// Computed properties converted to computed()
const firstOffer: any = computed(() => Object.values(props.lab.offers)[0] || {});

const worthTat = computed(() =>
  Math.max.apply(
    null,
    Object.values(props.lab.offers).map((el) => el.tat),
  ),
);

const initialsAndColor = computed(() => {
  const nameSplit = (props.lab.shopName || '').split(' ');
  let initials = nameSplit[0].charAt(0).toUpperCase();
  let colorIndex = 0;

  if (nameSplit[1]) {
    initials += nameSplit[1].charAt(0).toUpperCase();
    colorIndex = (initials.charCodeAt(0) + initials.charCodeAt(1)) % colors.length;
  } else {
    colorIndex = initials.charCodeAt(0) % colors.length;
  }

  return { initials, color: colors[colorIndex] };
});

const processFamiliesMap = computed(() => referential.mapProcessFamilies);
const materialFamiliesMap = computed(() => referential.mapMaterialFamillies);

const isPriceRange = (price: string) => price.includes(' - ') || price.slice(-1) === '+';

onMounted(async () => {
  primeLabs.value = application.primeLabs;
});
</script>

<template>
  <div
    v-mask="!lab || !lab.shopName"
    :class="['lab-card-container', { 'direct-purchase': directPurchase, selected: selected }]"
    @click="!directPurchase && emit('toggleSelected')"
  >
    <div v-if="lab" :class="['selected-container', { selected: selected }]">
      <div class="icon-container">
        <img
          v-if="lab.logoUrl"
          :alt="`Lab-icon-${lab.shopName}`"
          class="img-circle img-responsive"
          :src="lab.logoUrl"
          @error="emit('logo-error', lab)"
        />
        <div
          v-else
          class="logo-initials img-circle text-center"
          :style="{ 'background-color': initialsAndColor.color }"
        >
          {{ initialsAndColor.initials }}
        </div>
        <div v-if="primeLabs.map((el) => el.name).includes(lab.shopName)" class="lab-info-prime">
          <span class="lab-info-prime-text"> Prime </span>
        </div>
      </div>
      <div class="info-container">
        <div class="header">
          <span>
            <a
              :class="['title', { 'title-direct-purchase': directPurchase }]"
              :title="lab.shopName"
              @click.prevent.stop="
                emit('more-info', lab.storeURL);
                tracker?.event(
                  directPurchase
                    ? 'MoreInformationOnInstantQuoteLabClicked'
                    : 'MoreInformationOnLabClicked',
                  'Click on link',
                  'Get info',
                );
              "
            >
              {{ lab.shopName }}
            </a>
          </span>
          <div v-if="directPurchase" class="price">
            {{ lab.totalPrice }} {{ currencySymbol(firstOffer.currency) }}
          </div>
        </div>
        <div class="content">
          <div :class="['spec-column', { 'direct-purchase': directPurchase }]">
            <div v-if="country" class="spec country">
              <span class="desc country-text text--capitalize">
                <span class="fonticon fonticon-location" />{{ country.name.toLowerCase() }}
              </span>
            </div>
            <!-- Display offer information instead of more informations -->
            <div v-if="offerInfo" class="offer-information">
              <span
                :class="`fonticon fonticon-manufacturing-${processFamiliesMap[
                  lab.offers[0].processFamilyId
                ].name.toLowerCase()}`"
              />
              {{ processFamiliesMap[lab.offers[0].processFamilyId]?.name }}
              /
              {{ materialFamiliesMap[lab.offers[0].materialFamilyId]?.name }}
            </div>
            <div v-else class="more-info">
              <a
                class="btn-link"
                @click.prevent.stop="
                  emit('more-info', lab.storeURL);
                  tracker?.event(
                    directPurchase
                      ? 'MoreInformationOnInstantQuoteLabClicked'
                      : 'MoreInformationOnLabClicked',
                    'Click on link',
                    'Get info',
                  );
                "
              >
                {{ $i18n('moreinformation') }}
              </a>
            </div>
          </div>
          <div
            v-if="!directPurchase && !exploreMode"
            :class="['spec-column grid rfq-right-column column-gap-10px']"
          >
            <div class="spec leadtime display-contents-important">
              <span class="desc justify-self-end--sm">
                <span class="fonticon fonticon-clock" />
                {{ $i18n('lab_leadTime') }}
              </span>
              <span class="value justify-self-end"
                >{{ worthTat }} {{ worthTat > 1 ? $i18n('days') : $i18n('day') }}</span
              >
            </div>
            <div class="spec pricerange display-contents-important">
              <span class="desc justify-self-end--sm">
                <span class="fonticon fonticon-credit-card" />
                {{ isPriceRange(lab.totalPrice) ? $i18n('lab_priceRange') : $i18n('lab_price') }}
              </span>
              <span class="value justify-self-end"
                >{{ lab.totalPrice }} {{ firstOffer.currency }}</span
              >
            </div>
          </div>
          <div
            v-else-if="directPurchase"
            class="spec-column spec-column-direct-purchase text-right"
          >
            <div v-if="!loadingPrice" class="tat-text">
              <span class="fonticon fonticon-clock" />
              {{ worthTat }} {{ worthTat > 1 ? $i18n('days') : $i18n('day') }}
            </div>
            <div class="button-direct-purchase">
              <vu-btn v-if="loadingPrice" class="btn-with-icon">
                {{ $i18n('computing') }}
                <span class="fonticon fonticon-clock" />
              </vu-btn>
              <vu-btn v-else color="primary" @click="emit('buy')">
                {{ $i18n('buy') }}
              </vu-btn>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
section:not(.instant-purchase-section) .lab-card-container,
.quote-providers .lab-card-container {
  border: 1px solid #d1d4d4;
  border-radius: 5px;
}

section:not(.instant-purchase-section) .lab-card-container:hover,
.quote-providers .lab-card-container:hover {
  border: 1px solid #77797c;
}

section.instant-purchase-section .lab-card-container:not(:last-child) {
  border-bottom: solid 1px #d1d4d4;
}

.lab-card-container {
  display: flex;
  background-color: white;
  padding: 10px;
  position: relative;
}

.lab-card-container.selected {
  border: 1px solid #005686 !important;
  border-radius: 5px;
  background-color: rgba(120, 190, 250, 0.25);
  position: relative;
  overflow: hidden;
}

.lab-card-container.selected:before {
  content: '';
  width: 40px;
  height: 40px;
  position: absolute;
  transform: rotate(-45deg);
  top: -20px;
  left: -20px;
  background-color: #368ec4;
}

.lab-card-container.selected:after {
  content: '\e014';
  position: absolute;
  top: 0;
  left: 3px;
  color: white;
  font-family: entypo;
}

.lab-card-container .selected-container {
  width: 100%;
  display: flex;
}

.lab-card-container:not(.direct-purchase) {
  cursor: pointer;
}

.lab-card-container .icon-container {
  min-width: 44px;
  width: 44px;
  height: 44px;
  line-height: 44px;
  color: white;
}

.lab-card-container .icon-container .logo-initials {
  width: 100%;
  height: 100%;
}

.lab-card-container .icon-container img {
  height: inherit;
  width: inherit;
}

.lab-card-container .info-container {
  margin-left: 8px;
  flex-grow: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.lab-card-container .info-container .header {
  font-weight: bold;
  margin-bottom: 5px;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  color: #368ec4;
  display: flex;
  flex-direction: row;
}

.lab-card-container .info-container .header .title {
  color: #368ec4;
  font-size: 14px;
  flex-grow: 2;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  cursor: pointer;
}

.lab-card-container .info-container .header .price {
  font-size: 14px;
  font-weight: bold;
  color: #3d3d3d;
  flex-grow: 1;
  text-align: right;
}

.lab-card-container .info-container .header .title-direct-purchase {
  max-width: 69%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}

.lab-card-container .info-container .content {
  display: flex;
  flex-direction: row;
}

.lab-card-container .info-container .content .spec-column {
  flex-grow: 1;
  margin-top: 2px;
  max-width: 50%;
}

.lab-card-container
  .info-container
  .content
  .spec-column:not(.direct-purchase):not(.spec-column-direct-purchase) {
  max-width: none;
}

.lab-card-container .info-container .content .spec-column.rfq-right-column {
  grid-template-columns: repeat(2, minmax(0, 90px));
  flex-grow: initial;
  max-width: none;
  padding-right: 4px;
}

@media (max-width: 1023px) {
  .lab-card-container .info-container .content .spec-column.rfq-right-column {
    grid-template-columns: 1fr 3fr;
  }
}

.lab-card-container .info-container .content .spec-column.rfq-right-column .spec.pricerange .desc,
.lab-card-container .info-container .content .spec-column.rfq-right-column .spec.leadtime .desc {
  padding-top: 1px;
}

.lab-card-container .info-container .content .spec-column:not(:last-child) {
  margin-right: 10px;
}

.lab-card-container .info-container .content .spec-column .more-info {
  font-size: 12px;
  color: #368ec4;
  font-weight: bold;
}

.lab-card-container .info-container .content .spec-column .more-info .btn-link:hover {
  color: #368ec4;
}

.lab-card-container .info-container .content .spec-column .more-info .btn-link:active {
  color: #005686;
}

.lab-card-container .info-container .content .spec-column .spec {
  display: flex;
  flex-wrap: nowrap;
  justify-content: space-between;
}

.lab-card-container .info-container .content .spec-column .spec:not(:last-child) {
  margin-bottom: 5px;
}

.lab-card-container .info-container .content .spec-column .spec .desc {
  color: #b4b6ba;
  font-size: 12px;
}

.lab-card-container .info-container .content .spec-column .spec .desc.country-text {
  color: #77797c;
  max-width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  display: block;
}

.lab-card-container .info-container .content .spec-column .spec .desc .fonticon {
  margin-right: 5px;
  margin-left: 0;
}

.lab-card-container .info-container .content .spec-column .spec .value {
  font-weight: bold;
  color: #3d3d3d;
  font-size: 14px;
}

.lab-card-container .info-container .content .spec-column-direct-purchase {
  display: flex;
  flex-direction: row;
}

.lab-card-container .info-container .content .spec-column-direct-purchase .tat-text {
  flex-grow: 1;
  color: #77797c;
  font-size: 12px;
  vertical-align: top;
  text-align: left;
}

.lab-card-container .info-container .content .spec-column-direct-purchase .button-direct-purchase {
  flex-grow: 1;
}

.lab-card-container .lab-info-prime {
  transform: translate(1px, -12px);
}

.lab-card-container .offer-information {
  font-size: 12px;
  font-family: '3ds';
  font-weight: 600;
}

.lab-card-container .offer-information .fonticon {
  color: #77797c;
}

.lab-card-container .lab-info-prime-text {
  color: #00b8de;
  border: 1px solid #00b8de;
  border-radius: 4px;
  font-size: 10px;
  font-weight: normal;
  font-family: Arial;
  padding: 2px 7px;
}
</style>
